import { useLayoutEffect, useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import { useHistory } from "react-router-dom";
import { Box, Button, Grid, Link, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import {
	useStateContext,
	setLoadingViewAction,
} from "../../context/StateContext";
import GoogleIcon from "../../assets/GoogleIcon";
import emptyUserObj from "../../placeholders/userobj";
import emptyForgotPassObj from "../../placeholders/forgotPasswordObj";
import SignInBlock from "./signInBlocks/signIn";
import SignUpBlock from "./signInBlocks/signUp";
import checkPasswordStrength from "../../hooks/checkPasswordStrength";
import handleIdpLogin from "../../hooks/handleIdpLogin";
import ForgotPassSendBlock from "./signInBlocks/forgotPassSendBlock";
import ForgotPassConfirmBlock from "./signInBlocks/forgotPassConfirm";
import ForgotPassThankYou from "./signInBlocks/forgotPassThankYou";
import {
	AUTH_STRINGS,
	MAX_PASSWORD_STRENGTH,
	EMAIL_VERIFICATION_REGEXP,
} from "../../const";
import { GoogleRegister } from "../GoogleRegister/GoogleRegister";
import { formatWhitespaces, getErrorMessage } from "../../utils";
import { PageContainer } from "../../components";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import TagManager from "react-gtm-module";

const useStyles = makeStyles((theme) => ({
	grid: {
		flexDirection: "column",
		gap: "15px",
		width: "auto",
		paddingBottom: "30px",
	},
	fixedGrid: {
		flexDirection: "column",
		gap: "15px",
		paddingBottom: "30px",
		maxWidth: "427px",
	},
	auth: {
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
		padding: "25px",
		background: "#FFFFFF",
		border: "1px solid #DADADA",
		borderRadius: "4px",
	},
	pageHeader: {
		paddingTop: "19px",
		paddingBottom: "15px",
		fontSize: "30px",
		fontWeight: 600,
	},
	googleButton: {
		justifyContent: "flex-start",
		backgroundColor: "#4285F6",
		height: "45px",
		paddingLeft: "8px",
		"&:hover": {
			backgroundColor: "#4285F6",
		},
	},
	pageContainer: {
		[theme.breakpoints.up("lg")]: {
			maxWidth: "1121px",
			width: "100%",
		},
	},
	signInContainer: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "center",
		minHeight: "80vh",
		[theme.breakpoints.down("lg")]: {
			flexDirection: "column",
			alignItems: "center"
		},
	},
	signInTypographyBlock: {
		maxWidth: "694px",
		width: "100%",
		[theme.breakpoints.down("lg")]: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			textAlign: "center",
			paddingBottom: "40px",
		},
	},
}));

const SignInContainer = () => {
	const { state, dispatch } = useStateContext();
	const classes = useStyles();
	const [userObj, setUserObj] = useState(emptyUserObj);
	const [forgotPassObj, setForgotPassObj] = useState(emptyForgotPassObj);
	const [serverError, setServerError] = useState("");
	const [loading, setLoading] = useState(false);

	const navigation = useHistory();
	const location = useLocation();

	useEffect(() => {
		dispatch({
			type: "SET_LOADING_COOKIES",
			payload: false,
		});
	}, []);

	//Clear local storage and erorrs
	useLayoutEffect(() => {
		setLoading(false);
		setServerError("");

		if (state.view === "FORGOT_PASS") {
			setForgotPassObj(emptyForgotPassObj);
			return;
		}

		setUserObj(emptyUserObj);
	}, [state?.view]);

	//Amplify functions to handle Sign in, Sign up, Forgot password

	async function signIn(email, password) {
		try {
			setLoading(true);
			const user = await Auth.signIn(email, password);
			if (
				user.challengeName ===
				AUTH_STRINGS.signIn.challengeName.newPassword
			) {
				navigation.push("/new-password" + location.search, { email });
				return;
			}
			console.log("Show loading screen after successfully sign in.");
			dispatch(setLoadingViewAction());
		} catch (error) {
			if (
				error?.code ===
					AUTH_STRINGS.signIn.cognitoError.errorCode
						.notAuthorized &&
				error?.message ===
					AUTH_STRINGS.signIn.cognitoError.errorMessage
						.incorrectUsernameOrPassword
			) {
				// Invalid credentials only: keep the curated, user-friendly message that
				// points the user to password recovery, rather than the terser raw Cognito
				// "Incorrect username or password.". Other NotAuthorizedException cases
				// (e.g. a disabled user) keep their real message via getErrorMessage.
				setServerError(
					AUTH_STRINGS.signIn.cognitoError.errorMessage
						.incorrectCredentials
				);
			} else {
				setServerError(getErrorMessage(error));
			}
		} finally {
			setLoading(false);
		}
	}

	async function signUp(
		email,
		password,
		first_name,
		last_name,
		phone,
	) {
		try {
			setLoading(true);
			await Auth.signUp({
				username: email,
				password: password,
				attributes: {
					family_name: last_name,
					given_name: first_name,
					...(phone && { phone_number: phone }),
				},
				autoSignIn: {
					enabled: true,
				},
			});
			TagManager.dataLayer({
				dataLayer: {
					event: "signUp",
					userEmail: email,
				},
			});
			console.log("Show loading screen after sign up.");
			dispatch(setLoadingViewAction());
		} catch (err) {
			if (
				err.code ===
				AUTH_STRINGS.signUp.cognitoError.errorCode.invalidParameter
			) {
				switch (err.message) {
					case AUTH_STRINGS.signUp.cognitoError.errorMessage
						.invalidPhone:
						setUserObj({
							...userObj,
							phone: { value: phone, showError: true },
						});
						break;
					case AUTH_STRINGS.signUp.cognitoError.errorMessage
						.invalidEmail:
						setUserObj({
							...userObj,
							email: { value: email, showError: true },
						});
						break;
					default:
						break;
				}
			}

			if (
				err.code ===
				AUTH_STRINGS.signUp.cognitoError.errorCode.userExists
			) {
				setUserObj({
					...userObj,
					email: { value: email, showError: true },
				});
			}
			setServerError(getErrorMessage(err));
		} finally {
			setLoading(false);
		}
	}

	async function forgotPassword(email) {
		try {
			setLoading(true);
			await Auth.forgotPassword(email);
			dispatch({
				type: "SET_VIEW",
				payload: "FORGOT_PASS_CONFIRM",
			});
		} catch (err) {
			setServerError(getErrorMessage(err));
		} finally {
			setLoading(false);
		}
	}

	async function forgotPasswordSubmit(email, code, newPassword) {
		try {
			setLoading(true);
			await Auth.forgotPasswordSubmit(email, code, newPassword);
			dispatch({
				type: "SET_VIEW",
				payload: "FORGOT_PASS_THANKYOU",
			});
		} catch (err) {
			setServerError(getErrorMessage(err));
		} finally {
			setLoading(false);
		}
	}

	//Handeling presentational changes in inputs, checkboxes, etc for user data and forgot password data (separetely)

	function handleUserObjChange(newValue, property) {
		setServerError("");
		setUserObj({
			...userObj,
			[`${property}`]: {
				value: newValue,
				...(property !== "phone" && { showError: false }),
			},
		});
	}

	function handleForgotPassObjChange(newValue, property) {
		setServerError("");
		setForgotPassObj({
			...forgotPassObj,
			passwordsMatch: {
				showError: false,
			},
			[`${property}`]: {
				value: newValue,
				showError: false,
			},
		});
	}

	function handleSubmit(type) {
		if (type === "SIGN_IN") {
			if (!userObj.email.value || !userObj.password.value) {
				setServerError(
					AUTH_STRINGS.signIn.emptyCredentials.errorMessage
						.emptyUsernameOrPassword
				);
				return;
			}

			signIn(
				formatWhitespaces(userObj.email.value),
				formatWhitespaces(userObj.password.value)
			);
		}

		if (type === "SIGN_UP") {
			let modifiedUserObj = {};
			let filteredUserObj = {
				...userObj,
				email: {
					...userObj.email,
					value: formatWhitespaces(userObj.email.value),
				},
				first_name: {
					...userObj.first_name,
					value: formatWhitespaces(userObj.first_name.value),
				},
				last_name: {
					...userObj.last_name,
					value: formatWhitespaces(userObj.last_name.value),
				},
			};

			Object.entries(filteredUserObj).forEach(
				([keyUserObj, valueUserObj]) => {
					if (keyUserObj === "phone") {
						modifiedUserObj = {
							...modifiedUserObj,
							phone: { value: valueUserObj.value },
						};
						return;
					}

					let error = false;

					if (!valueUserObj.value) {
						error = true;
					}

					if (
						keyUserObj === "password" &&
						checkPasswordStrength(valueUserObj.value) !==
							MAX_PASSWORD_STRENGTH
					) {
						error = true;
					}

					if (
						keyUserObj === "email" &&
						!EMAIL_VERIFICATION_REGEXP.test(valueUserObj.value)
					) {
						error = true;
					}

					modifiedUserObj = {
						...modifiedUserObj,
						[`${keyUserObj}`]: {
							...valueUserObj,
							showError: error,
						},
					};
				}
			);
			setUserObj({ ...modifiedUserObj });
			const isValid = Object.values(modifiedUserObj).every(
				({ showError }) => !showError
			);
			if (isValid) {
				signUp(
					modifiedUserObj.email.value,
					modifiedUserObj.password.value,
					modifiedUserObj.first_name.value,
					modifiedUserObj.last_name.value,
					modifiedUserObj.phone.value,
				);
			}
		}

		if (type === "FORGOT_PASS") {
			const inputEmail = formatWhitespaces(forgotPassObj.email.value);
			setForgotPassObj({
				...forgotPassObj,
				email: {
					value: inputEmail,
				},
			});
			forgotPassword(inputEmail);
		}

		if (type === "FORGOT_PASS_VERIFICATION") {
			let filteredPassObj = {};
			filteredPassObj = {
				email: { value: forgotPassObj.email.value },
				passwordsMatch: {
					...forgotPassObj.passwordsMatch,
					showError:
						forgotPassObj.newPassword.value !==
						forgotPassObj.newPasswordRepeated.value,
				},
				verificationCode: {
					...forgotPassObj.verificationCode,
					value: formatWhitespaces(
						forgotPassObj.verificationCode.value
					),
					showError: !formatWhitespaces(
						forgotPassObj.verificationCode.value
					)
						? true
						: false,
				},
			};

			Object.entries(forgotPassObj).forEach(
				([keyUserObj, valueUserObj]) => {
					if (
						keyUserObj === "email" ||
						keyUserObj === "passwordsMatch" ||
						keyUserObj === "verificationCode"
					) {
						return;
					}
					let error = false;

					if (
						!forgotPassObj[`${keyUserObj}`].value ||
						checkPasswordStrength(
							forgotPassObj[`${keyUserObj}`].value
						) !== MAX_PASSWORD_STRENGTH
					) {
						error = true;
					}

					filteredPassObj = {
						...filteredPassObj,
						[`${keyUserObj}`]: {
							...forgotPassObj[`${keyUserObj}`],
							showError: error,
						},
					};
				}
			);

			setForgotPassObj({
				...filteredPassObj,
			});

			if (
				!filteredPassObj.verificationCode.showError &&
				!filteredPassObj.newPassword.showError &&
				!filteredPassObj.newPasswordRepeated.showError &&
				!filteredPassObj.passwordsMatch.showError
			) {
				forgotPasswordSubmit(
					filteredPassObj.email.value,
					filteredPassObj.verificationCode.value,
					filteredPassObj.newPassword.value
				);
			}
		}
	}

	const forgotPassBlock = (
		<Box className={classes.auth}>
			{state.view === "FORGOT_PASS" && (
				<ForgotPassSendBlock
					forgotPassObj={forgotPassObj}
					handleForgotPassObjChange={handleForgotPassObjChange}
					loading={loading}
					handleSubmit={handleSubmit}
					errorMessage={serverError}
				/>
			)}
			{state.view === "FORGOT_PASS_CONFIRM" && (
				<ForgotPassConfirmBlock
					forgotPassObj={forgotPassObj}
					handleForgotPassObjChange={handleForgotPassObjChange}
					loading={loading}
					handleSubmit={handleSubmit}
					errorMessage={serverError}
				/>
			)}
			{state.view === "FORGOT_PASS_THANKYOU" && (
				<ForgotPassThankYou loading={loading} />
			)}
		</Box>
	);

	const handleSignUpViaGoogle = () => {
		try {
			handleIdpLogin("Google");
		} catch (error) {
			// handleIdpLogin triggers a federated redirect; nothing to recover here
		}
	};

	const setPasswordValidation = (validation) => {
		setUserObj({
			...userObj,
			password: {
				value: userObj.password.value,
				showError: validation
			}
		});
	}

	switch (state.view) {
		case "SIGN_IN":
			return (
				<PageContainer className={classes.pageContainer}>
					<Box
						className={classes.signInContainer}
					>
						<Box className={classes.signInTypographyBlock}>
							<Typography
								variant="h1"
								className={classes.pageHeader}
							>
								{"Welcome to your 3T Account"}
							</Typography>
							<Typography
								sx={{
									maxWidth: "557px",
									fontSize: "20px",
									paddingBottom: "10px",
								}}
							>
								{
									"Please log in to access the full range of 3T services: activate your copy of Studio 3T, join our community, and manage your license."
								}
							</Typography>

							<Typography
								sx={{
									maxWidth: "557px",
								}}
							>
								Don’t have an account, yet?{" "}
								<Link
									onClick={() =>
										dispatch({
											type: "SET_VIEW",
											payload: "SIGN_UP",
										})
									}
								>
									Sign up.
								</Link>
							</Typography>
						</Box>
						<Grid container className={classes.fixedGrid}>
							<SignInBlock
								userObj={userObj}
								handleUserObjChange={handleUserObjChange}
								loading={loading}
								handleSubmit={handleSubmit}
								errorMessage={serverError}
							/>
							<Button
								className={classes.googleButton}
								startIcon={<GoogleIcon />}
								variant={"contained"}
								onClick={handleSignUpViaGoogle}
							>
								{"Sign in with Google"}
							</Button>
						</Grid>
					</Box>
				</PageContainer>
			);
		case "SIGN_UP":
			return (
				<PageContainer>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							flexWrap: "wrap",
							justifyContent: "center",
							alignItems: "center",
							minHeight: "80vh",
						}}
					>
						<Grid container className={classes.grid}>
							<SignUpBlock
								userObj={userObj}
								handleUserObjChange={handleUserObjChange}
								loading={loading}
								handleSubmit={handleSubmit}
								errorMessage={serverError}
								setPasswordValidation={setPasswordValidation}
							/>

							<Button
								className={classes.googleButton}
								startIcon={<GoogleIcon />}
								variant={"contained"}
								onClick={handleSignUpViaGoogle}
							>
								{"Sign up with Google"}
							</Button>
						</Grid>
					</Box>
				</PageContainer>
			);
		case "GOOGLE_SIGN_UP":
			return <GoogleRegister />;
		case "FORGOT_PASS":
		case "FORGOT_PASS_CONFIRM":
		case "FORGOT_PASS_THANKYOU":
			return (
				<PageContainer>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							flexWrap: "wrap",
							alignItems: "center",
							minHeight: "80vh",
						}}
					>
						<Box
							sx={{
								maxWidth: "694px",
								width: "100%",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								textAlign: "center",
								paddingBottom: "40px",
							}}
						>
							<Typography
								variant="h1"
								className={classes.pageHeader}
							>
								{"Forgot your password?"}
							</Typography>
						</Box>
						<Grid container className={classes.fixedGrid}>
							{forgotPassBlock}
						</Grid>
					</Box>
				</PageContainer>
			);
		default:
			return null;
	}
};

export default SignInContainer;
